import {
    ApplicationConfig,
    importProvidersFrom,
    inject,
    PLATFORM_ID,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection,
    APP_INITIALIZER,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import {
    HTTP_INTERCEPTORS,
    HttpBackend,
    provideHttpClient,
    withFetch,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import packageJson from 'package.json';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { ModalModule } from 'ngx-bootstrap/modal';
import { Interceptor } from './core/services/interceptor.service';
import { provideAnimations } from '@angular/platform-browser/animations';
import { daLocale, defineLocale, fiLocale, nbLocale, svLocale } from 'ngx-bootstrap/chronos';
import { environment } from '@environment';
import { isPlatformServer } from '@angular/common';
import { QuillHelperService } from '@sb-tools/typescript/services/quill/quill-helper.service';

defineLocale('nb', nbLocale);
defineLocale('sv', svLocale);
defineLocale('da', daLocale);
defineLocale('fi', fiLocale);

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpBackend: HttpBackend) {
    const platformId = inject(PLATFORM_ID);
    const baseUrl = isPlatformServer(platformId) ? environment.formUrl.slice(0, -1) : '';
    return new MultiTranslateHttpLoader(httpBackend, [
        { prefix: baseUrl + '/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: baseUrl + '/sb-tools/assets/i18n/', suffix: '.json?' + packageJson.version },
        {
            prefix: baseUrl + '/sb-modules/Frontend-form-field-module/assets/i18n/',
            suffix: '.json?' + packageJson.version,
        },
        {
            prefix: baseUrl + '/sb-modules/Frontend-Cookie-Law-Banner-Module/assets/i18n/',
            suffix: '.json?' + packageJson.version,
        },
        { prefix: baseUrl + '/sb-modules/Frontend-Footer-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
        {
            prefix: baseUrl + '/sb-modules/Frontend-Reply-Templates-Module/assets/i18n/',
            suffix: '.json?' + packageJson.version,
        },
        {
            prefix: baseUrl + '/sb-modules/Frontend-User-Avatar-Module/assets/i18n/',
            suffix: '.json?' + packageJson.version,
        },
    ]);
}

// Quill initialization factory
export function initQuill(quillHelperService: QuillHelperService) {
    return () => quillHelperService.initQuillRegisters();
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes),
        provideHttpClient(withFetch(), withInterceptorsFromDi()),
        importProvidersFrom(
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpBackend],
                },
            }),
            SimpleNotificationsModule.forRoot({
                position: ['bottom', 'right'],
                timeOut: 10000,
                lastOnBottom: true,
                showProgressBar: true,
                pauseOnHover: true,
            }),
            ModalModule.forRoot(),
        ),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: Interceptor,
            multi: true,
        },
        provideAnimations(),
        {
            provide: APP_INITIALIZER,
            useFactory: initQuill,
            multi: true,
            deps: [QuillHelperService],
        },
    ],
};
